// 人と人の縁（Phase A）— クライアント側のフック
//   useEnConfig    config/en（キルスイッチ・段階公開）
//   useEnSettings  users/{uid}/enSettings/main（sodeOpen / hiddenShrineIds）
//   useMyVisits    users/{uid}/visits
//   useSharedCount countSharedVisitors（自分が参った神社に、ほかに何人が参っているか。人数だけ）
// 言葉の約束: 「出会い」「マッチング」「袖振り合う」は UI に出さない。
import { useCallback, useEffect, useState } from "react";
import { doc, onSnapshot, collection, setDoc, updateDoc, serverTimestamp, query, orderBy, limit } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../api/firebase";
import { useAuth } from "../../context/auth";

export function useEnConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState({ enabled: false, allowlistUids: [], loaded: false });
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config", "en"),
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setConfig({
          enabled: d.enabled === true,
          allowlistUids: Array.isArray(d.allowlistUids) ? d.allowlistUids : [],
          loaded: true,
        });
      },
      () => setConfig({ enabled: false, allowlistUids: [], loaded: true })
    );
    return unsub;
  }, []);
  const allowed =
    config.enabled && (!config.allowlistUids.length || (user && config.allowlistUids.includes(user.uid)));
  return { ...config, allowed: !!allowed };
}

export function useEnSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ sodeOpen: false, hiddenShrineIds: [], loaded: false });
  useEffect(() => {
    if (!user) {
      setSettings({ sodeOpen: false, hiddenShrineIds: [], loaded: true });
      return undefined;
    }
    const unsub = onSnapshot(doc(db, "users", user.uid, "enSettings", "main"), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setSettings({
        sodeOpen: d.sodeOpen === true,
        hiddenShrineIds: Array.isArray(d.hiddenShrineIds) ? d.hiddenShrineIds : [],
        loaded: true,
      });
    });
    return unsub;
  }, [user]);

  const setSodeOpen = useCallback(
    async (open) => {
      if (!user) return;
      await setDoc(
        doc(db, "users", user.uid, "enSettings", "main"),
        {
          sodeOpen: !!open,
          hiddenShrineIds: settings.hiddenShrineIds,
          ...(open ? { openedAt: serverTimestamp() } : {}),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    },
    [user, settings.hiddenShrineIds]
  );
  return { ...settings, setSodeOpen };
}

export function useMyVisits() {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!user) {
      setVisits([]);
      setLoaded(true);
      return undefined;
    }
    const q = query(collection(db, "users", user.uid, "visits"), orderBy("createdAt", "desc"), limit(200));
    const unsub = onSnapshot(q, (snap) => {
      setVisits(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoaded(true);
    });
    return unsub;
  }, [user]);

  // 記憶の参拝: 日付不明は "0000-00-00"。docId は <shrineId>_<day>（rules で固定）
  const addMemory = useCallback(
    async (shrineId, day = "0000-00-00") => {
      if (!user) return;
      await setDoc(doc(db, "users", user.uid, "visits", `${shrineId}_${day}`), {
        shrineId,
        day,
        source: "memory",
        createdAt: serverTimestamp(),
      });
    },
    [user]
  );
  return { visits, loaded, addMemory };
}

export function useSharedCount(enabled, deps = []) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const refresh = useCallback(async () => {
    if (!enabled) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const fn = httpsCallable(functions, "countSharedVisitors");
      const res = await fn({});
      setState({ loading: false, data: res.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e });
    }
  }, [enabled]);
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, ...deps]);
  return { ...state, refresh };
}

/**
 * 降りた縁（Phase B）。users/{uid}/drops を新しい順に購読し、生きているもの（new / waved / matched）を返す。
 * wave(id)＝縁を結びに袖を振る、pass(id)＝見送る。rules で state は new → waved | passed しか進められない。
 */
export function useDrops() {
  const { user } = useAuth();
  const [drops, setDrops] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!user) {
      setDrops([]);
      setLoaded(true);
      return undefined;
    }
    const q = query(collection(db, "users", user.uid, "drops"), orderBy("droppedAt", "desc"), limit(10));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        setDrops(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((d) => ["new", "waved", "matched"].includes(d.state))
            .filter((d) => !(d.state === "new" && d.expiresAt?.toMillis && d.expiresAt.toMillis() < now))
        );
        setLoaded(true);
      },
      () => {
        setDrops([]);
        setLoaded(true);
      }
    );
    return unsub;
  }, [user]);

  const respond = useCallback(
    async (dropId, state) => {
      if (!user) return;
      await updateDoc(doc(db, "users", user.uid, "drops", dropId), { state, respondedAt: serverTimestamp() });
    },
    [user]
  );
  const today = drops.find((d) => d.state === "new") || null;
  return {
    drops,
    today,
    loaded,
    wave: (id) => respond(id, "waved"),
    pass: (id) => respond(id, "passed"),
  };
}
